// components/Outreach/IntentionsList.js
import React, { useState, useEffect } from "react";
import { getCookie } from "../../services/cookie";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

const API_BASE = "https://flautreach.com/flautreach/api";

const STATUS_META = {
  PENDING: { color: "#f59e0b", bg: "#fef3c7", label: "Ожидает" },
  IN_PROGRESS: { color: "#3b82f6", bg: "#eff6ff", label: "В работе" },
  DONE: { color: "#16a34a", bg: "#f0fdf4", label: "Готово" },
  FAILED: { color: "#dc2626", bg: "#fef2f2", label: "Ошибка" },
  SKIPPED: { color: "#6b7280", bg: "#f3f4f6", label: "Пропущено" },
};

export function IntentionsList({ onRefresh, add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  const [intentions, setIntentions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedRaw, setExpandedRaw] = useState({});

  useEffect(() => { loadIntentions(); }, [filter, page]);

  const loadIntentions = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/outreach/intentions/?page=${page}&page_size=20`;
      if (filter !== "all") url += `&status=${filter}`;
      const resp = await fetch(url, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        const list = data.results || data || [];
        setIntentions(Array.isArray(list) ? list : []);
        setTotalPages(data.pagination?.total_pages || 1);
      }
    } catch (err) {
      add?.("Ошибка загрузки намерений", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleRawData = (id) => {
    setExpandedRaw(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDeleteIntention = async (id) => {
    if (!window.confirm("Удалить это намерение?")) return;
    try {
      const resp = await fetch(`${API_BASE}/outreach/intentions/${id}/`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (resp.ok) {
        add?.("Намерение удалено", "info");
        loadIntentions();
        onRefresh?.();
      } else {
        const err = await resp.json().catch(() => ({}));
        add?.(err.detail || "Ошибка удаления", "error");
      }
    } catch (err) {
      add?.("Ошибка удаления", "error");
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><div className="spinner"/></div>;

  return (
    <div>
      <div className="rules-header" style={{ marginTop: 0 }}>
        <div>
          <h2>Намерения</h2>
          <p>Фильтр по статусу</p>
        </div>
        <select className="form-input" style={{ width: "auto", paddingLeft: 14 }} value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}>
          <option value="all">Все</option>
          <option value="PENDING">Ожидают</option>
          <option value="IN_PROGRESS">В работе</option>
          <option value="DONE">Выполнены</option>
          <option value="FAILED">С ошибкой</option>
          <option value="SKIPPED">Пропущены</option>
        </select>
      </div>

      {intentions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Нет намерений</h3>
          <p>Создайте намерения через кампании</p>
        </div>
      ) : (
        <>
          <div className="rules-grid">
            {intentions.map((inv) => {
              const s = STATUS_META[inv.status] || STATUS_META.PENDING;
              const rawData = inv.target_data || {};
              const hasRawData = rawData && Object.keys(rawData).length > 0;
              const isExpanded = expandedRaw[inv.id];

              return (
                <div key={inv.id} className="rule-card">
                  <div className="rule-card-top">
                    <div className="rule-intent">
                      <p style={{ fontSize: 14 }}>
                        {inv.campaign_name || `Кампания #${inv.campaign}`}
                      </p>
                      <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, wordBreak: "break-all" }}>
                        🎯 {inv.target_url?.substring(0, 80) || `Target #${inv.target}`}
                      </p>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                        📡 Каналы: {(inv.candidate_channels || []).join(", ")}
                        {" · "}{inv.action_type}
                      </div>
                    </div>
                    <div className="rule-status-badge" style={{ background: s.bg, color: s.color }}>
                      <span className="rule-dot" style={{ background: s.color }}/>
                      {s.label}
                    </div>
                  </div>

                  {inv.payload && Object.keys(inv.payload).length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
                      {Object.entries(inv.payload).map(([ch, data]) => (
                        <div key={ch} style={{ marginTop: 4 }}>
                          <strong>{ch}:</strong>{" "}
                          {typeof data === "object"
                            ? (data.subject ? `Тема: ${data.subject} | ` : "") + (data.body?.substring(0, 100) || "")
                            : String(data).substring(0, 100)}
                        </div>
                      ))}
                    </div>
                  )}

                  {hasRawData && (
                    <div style={{ marginTop: 10 }}>
                      <button
                        onClick={() => toggleRawData(inv.id)}
                        style={{ fontSize: 11, fontWeight: 600, color: "var(--blue)", background: "none", border: "none", cursor: "pointer", padding: "4px 0" }}
                      >
                        {isExpanded ? "🔼 Скрыть raw_data" : "🔽 Показать raw_data"}
                      </button>
                      {isExpanded && (
                        <pre style={{ marginTop: 8, padding: 12, background: "#f8f9ff", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 300, overflow: "auto", fontFamily: "monospace", color: "#334155", lineHeight: 1.5 }}>
                          {JSON.stringify(rawData, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}

                  {inv.attempts?.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
                      Попытки: {inv.attempts.map((a, i) => (
                        <span key={i} style={{ marginLeft: 8 }}>{a.channel}: {a.status === "success" ? "✅" : "❌"}</span>
                      ))}
                    </div>
                  )}

                  <div className="rule-meta">
                    <span>⭐ Приоритет: {inv.priority}</span>
                    <span>🔄 Попыток: {inv.attempts?.length || 0}</span>
                    {inv.scheduled_for && <span>🕐 {new Date(inv.scheduled_for).toLocaleString()}</span>}
                  </div>

                  <div className="rule-actions">
                    <button className="btn-delete" onClick={() => handleDeleteIntention(inv.id)}>
                      🗑 Удалить
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "12px 0", marginTop: 10 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                style={{ minWidth: 36, height: 36, borderRadius: 8, border: "1.5px solid var(--border)", background: "#fff", cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <HiChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  style={{ minWidth: 36, height: 36, borderRadius: 8, border: "none", background: p === page ? "var(--blue)" : "transparent", color: p === page ? "#fff" : "var(--dark)", fontWeight: p === page ? 600 : 400, cursor: "pointer", fontSize: 14 }}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ minWidth: 36, height: 36, borderRadius: 8, border: "1.5px solid var(--border)", background: "#fff", cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <HiChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}